import React, { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import style from "./GraficaVisitor.module.css";
import iconFuerza from "../../../assets/iconFuerza.png";
import {
  getChartOptions,
  getUnidad,
} from "../../Jugador/Estadisticas/Modales/ModalMetrica/utils/getChartOptions";
import {
  arrayFechas,
  arrayValores,
  fechaFormateada,
} from "../../Jugador/Estadisticas/Modales/ModalMetrica/utils/metricsUtils";
import { filterMetricas } from "../../Jugador/Estadisticas/Modales/ModalMetrica/utils/filterMetricas";
import VerificarMetrica from "../../Jugador/Estadisticas/Modales/ModalMetrica/Componentes/VerificarMetrica";
import iconFilled from "../../../assets/filled.png";

const GraficaVisitor = ({
  user,
  metricaV,
  comparativoBool,
  firstSixPosicion,
}) => {
  const [editBool, setEditBool] = useState(true);

  // Filtra las métricas del usuario según la métrica seleccionada
  const filteredData = useMemo(
    () => filterMetricas(user, metricaV),
    [user, metricaV]
  );

  // Extrae los datos actuales desde los datos filtrados
  const currentData = useMemo(
    () => ({
      valor: filteredData[0]?.valor || 0,
      createdAt: filteredData[0]?.createdAt,
      verificaPositivo: filteredData[0]?.verificaPositivo || 0,
      verificaNegativo: filteredData[0]?.verificaNegativo || 0,
    }),
    [filteredData]
  );

  // Configuración del gráfico con los datos correspondientes
  const chartConfig = useMemo(
    () => ({
      labels: comparativoBool
        ? arrayFechas(firstSixPosicion) || ["sin valores"]
        : arrayFechas(filteredData) || ["sin valores"],
      datasets: [
        {
          label: ` - ${metricaV.charAt(0).toUpperCase() + metricaV.slice(1)}`,
          data: comparativoBool
            ? arrayValores(firstSixPosicion) || [0]
            : arrayValores(filteredData) || [0],
          fill: false,
          borderColor: "rgba(228, 253, 63, 1)",
          backgroundColor: "rgba(228, 253, 63, 1)",
          borderWidth: 3,
          tension: 0.5,
          // Agregar los datos de validadores para el tooltip
          validadores: comparativoBool
            ? (firstSixPosicion || []).map(
                (item) => item.validadores?.length || 0
              )
            : (filteredData || []).map((item) => item.validadores?.length || 0),
        },
      ],
    }),
    [comparativoBool, firstSixPosicion, filteredData]
  );

  // Opciones de configuración del gráfico
  const chartOptions = useMemo(
    () => ({
      ...getChartOptions(metricaV),
      maintainAspectRatio: false,
      responsive: true,
      animation: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(21, 26, 32, 1)",
          },
          ticks: {
            color: "white",
            font: {
              family: "Raleway",
            },
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "white",
            font: {
              family: "Raleway",
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (context) => {
              return `Fecha: ${context[0].label}`;
            },
            label: (context) => {
              const dataset = context.dataset;
              const dataIndex = context.dataIndex;
              const valor = context.raw;
              const validadores = dataset.validadores?.[dataIndex] || 0;

              return [
                `${dataset.label}: ${valor} ${getUnidad(metricaV)}`,
                `Verificado: ${validadores} veces`,
              ];
            },
          },
        },
      },
    }),
    [metricaV]
  );

  // Renderiza el gráfico en función del tipo de métrica
  const ChartComponent = ({ type }) => {
    const ChartType = type === "line" ? Line : Bar;
    return (
      <div className={style.chartContainer}>
        <ChartType data={chartConfig} options={chartOptions} />
      </div>
    );
  };

  return (
    <div className={style.contGrafica}>
      <div className={style.contTitle}>
        <img
          className={style.iconFuerza}
          src={iconFuerza}
          alt="Ícono de fuerza"
        />
        <h2 className={style.title}>{metricaV.toUpperCase()}</h2>
        <div className={style.validacionesBox}>
          <img className={style.validacionesIcon} src={iconFilled} alt="" />
          <span className={style.validacionesTexto}>
            Veces validada:{" "}
            <strong>
              {currentData.verificaPositivo + currentData.verificaNegativo}
            </strong>
          </span>
        </div>
      </div>

      <div className={style.contInfo}>
        <div className={style.infoC}>
          <h4 className={style.sub}>ACTUAL</h4>
          <div className={style.contData}>
            <h1 className={style.data}>{currentData.valor}</h1>
            <h3 className={style.unidad}>{getUnidad(metricaV)}</h3>
          </div>
        </div>

        <div className={style.contFecha}>
          <div className={style.contSubfecha}>
            <p className={style.subFecha}>Última edición</p>
            <p className={style.fecha}>
              {currentData.createdAt && fechaFormateada(currentData.createdAt)}
            </p>
          </div>

          {!editBool ? (
            <VerificarMetrica
              filterMetricas={filterMetricas}
              metricaV={metricaV}
              editBool={editBool}
              setEditBool={setEditBool}
              user={user}
            />
          ) : (
            <button className={style.btnG} onClick={() => setEditBool(false)}>
              Verificar
            </button>
          )}
        </div>
      </div>

      {editBool && (
        <div className={style.conSubGrafico}>
          <h4 className={style.sub}>HISTÓRICO</h4>
          <ChartComponent
            type={
              metricaV === "fuerza" || metricaV === "resistencia"
                ? "line"
                : "bar"
            }
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(GraficaVisitor);
